import React, { Suspense, useCallback } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { AppContextProvider } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';

import ExpertsBlock from './Blocks/ExpertsBlock';
import HTMLBlock from './Blocks/HTMLBlock';
import HorizontalRuleBlock from './Blocks/HorizontalRuleBlock';
import ImageBlock from './Blocks/ImageBlock';
import RecommendedArticlesBlock from './Blocks/RecommendedArticleBlock';
import RenderTiptapContent from './Blocks/RenderTiptapContent';
import SectionBlock from './Blocks/SectionBlock';
import SubscribeMatrixBlock from './Blocks/SubscribeMatrixBlock';
import TextBlock from './Blocks/TextBlock';
import VideoBlock from './Blocks/VideoBlock';
import { BlockTypes } from './Blocks/constants';
import WithAnalyticsWrapper from './WithAnalyticsWrapper';
import { blocksWithoutContent } from './constants';
import type { PageTemplateJSON, PageTemplateProps, RecursiveValue } from './interfaces';
import './styles.scss';

const queryClient = new QueryClient();

const PageTemplates = ({
  offers,
  recommendedArticles,
  reporters,
  templateJSON,
  templateMetadata,
  ...props
}: PageTemplateProps) => {
  const { Track } = useEventTracking({
    ...(templateMetadata?.page_analytics ? { page: templateMetadata.page_analytics } : {}),
  });

  const RenderedTemplate = useCallback((blocks: PageTemplateJSON[]) => {
    if (blocks.length === 0) return;

    return blocks?.map((block) => {
      const contentBlock =
        (block.content?.json !== undefined
          ? block.content.json
          : block?.children
          ? block.children
          : block?.content) ?? {};
      const config = block?.config;
      const type = block.type;

      if (!Object.keys(contentBlock).length && !blocksWithoutContent.includes(type as BlockTypes)) {
        return null;
      }

      switch (type) {
        case BlockTypes.Image:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <ImageBlock
                config={config}
                content={contentBlock as Record<string, RecursiveValue>}
              />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.Section:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <SectionBlock config={config}>
                {RenderedTemplate(contentBlock as PageTemplateJSON[])}
              </SectionBlock>
            </WithAnalyticsWrapper>
          );
        case BlockTypes.TextContainer:
          // Don't wrap in WithAnalyticsWrapper because it's already wrapped in RenderTiptapContent
          return (
            <TextBlock key={block.id} config={config}>
              <RenderTiptapContent
                config={config}
                contentBlock={contentBlock as Record<string, RecursiveValue>}
              />
            </TextBlock>
          );
        case BlockTypes.Video:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <VideoBlock
                config={config}
                content={contentBlock as Record<string, RecursiveValue>}
              />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.Newsletter:
          return <div key={block.id}>Newsletter Block</div>;
        case BlockTypes.SubscribeMatrix:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <SubscribeMatrixBlock config={config} offers={offers} />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.RecommendedArticles:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <RecommendedArticlesBlock recommendedArticles={recommendedArticles} />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.Experts:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <ExpertsBlock config={config} reporters={reporters} />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.HTML:
          return (
            <WithAnalyticsWrapper key={block.id} config={config}>
              <HTMLBlock html={(contentBlock as Record<string, RecursiveValue>).html as string} />
            </WithAnalyticsWrapper>
          );
        case BlockTypes.HorizontalRule:
          return <HorizontalRuleBlock key={block.id} config={config} />;
        default:
          return (
            <React.Fragment key={block.id}>
              <RenderTiptapContent
                config={config}
                contentBlock={contentBlock as Record<string, RecursiveValue>}
              />
            </React.Fragment>
          );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppContextProvider {...props}>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<div>Loading…</div>}>
          <Track>
            <main className="relative px-16 sm:px-28">
              <div className="m-auto max-w-container-lg">
                {RenderedTemplate(templateJSON.blocks)}
              </div>
            </main>
          </Track>
        </Suspense>
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export default PageTemplates;
